import { Injectable } from "@angular/core";
import { map, Observable, of } from "rxjs";

import { AgentCompaniesSelected, AgentCompany, AgentCompanyList, AgentCreateMemberModel, AgentCreateMemberPayload, AgentCreateMemberResponse, AgentMemberSearchHistory, AgentMemberSearchResult, AgentsManageDisabled, AgentUser, AgentUserFilterPayload, AgentCarrierInfo, AgentCarrierInfoPayload, CompanyAgentsSelected, AgentFilterPayload, SupervisedAgentList } from "src/app/models/agent";
import { StoreNames } from "src/app/constants/storage-names";

import { SessionService } from './storage/session.service';
import { ApiService } from "./api.service";
import { SessionStatus } from "src/app/constants/session";

import { ToastService } from './toast.service';
import { Broker, WorkingBroker } from "src/app/models/broker";
import { ToastType } from "src/app/constants/toast-type";
import { Carrier, CarrierPayload } from "../models/carrier";
import { ServerConnectionAuthModel, ServerConnectionModel } from "../models/test-server";
import { Authenticator, AuthenticatorQR, AuthenticatorRequest } from "../models/authenticator";

const controllerRoute = '/agents';
const controllerManageRoute = '/agents-manage';

@Injectable()
export class AgentService {

    public agentEnabledCompanies: AgentCompany[];
    public sessionStore: Observable<any>;
    public brokers: Broker[];

    constructor(
        private apiService: ApiService,
        private sessionService: SessionService,
        private toastService: ToastService,
    ) {
        this.sessionStore = this.sessionService.$SessionClear;
        this.sessionStore.subscribe((status: string) => {
            switch (status) {
                case SessionStatus.initalize:
                    let sessionState = this.sessionService.retrieve(StoreNames.agentEnabledCompanies);
                    if (sessionState)
                        this.agentEnabledCompanies = sessionState;
                    break;

                case SessionStatus.clearSession:
                    this.agentEnabledCompanies = null;
                    this.brokers = null;
                    break;
            }
        });
    }

    public getAgentEnabledCompanies(): Observable<AgentCompany[]> {
        if (this.agentEnabledCompanies)
            return of(this.agentEnabledCompanies);

        return this.apiService.get(`${controllerRoute}/enabled-companies`).pipe(
            map((companyList: AgentCompanyList) => {
                this.agentEnabledCompanies = companyList.agentCompanies;
                this.sessionService.store(StoreNames.agentEnabledCompanies, this.agentEnabledCompanies);
                return this.agentEnabledCompanies;
            }),
        );
    }

    public getEnabledCompaniesByWorkingBroker(): Observable<AgentCompany[]> {
        return this.apiService.get(`${controllerRoute}/enabled-companies-working-broker`).pipe(
            map((companyList: AgentCompanyList) => {
                return companyList.agentCompanies;
            }),
        );
    }

    public getAgentMemberSearchHistory(): Observable<AgentMemberSearchResult[]> {
        return this.apiService.get(`${controllerRoute}/member-search-history`).pipe(
            map((searchList: AgentMemberSearchHistory) => {
                return searchList.matchingMembers;
            }),
        );
    }

    public setWorkingBroker(payload: WorkingBroker): Observable<Broker> {
        return this.apiService.post(`${controllerManageRoute}/update-working-broker`, payload).pipe(
            map((response: Broker) => {
                this.sessionService.session.selectedBrokerId = payload.Id;
                this.sessionService.setSession();
                return response;
            }),
        );
    }

    public getSupervisedAgentsFromSetByWorkingBroker(payload: AgentFilterPayload): Observable<SupervisedAgentList> {
        return this.apiService.post(`${controllerManageRoute}/supervised-agents`, payload).pipe(
            map((response: SupervisedAgentList) => {
                return response;
            }),
        );
    }

    public getBrokers(): Observable<Broker[]> {
        if (this.brokers)
            return of(this.brokers);

        return this.apiService.get(`${controllerManageRoute}/get-brokers`).pipe(
            map((response: Broker[]) => {
                this.brokers = response;
                return this.brokers;
            }),
        );
    }

    public getAgents(payload: AgentUserFilterPayload): Observable<AgentUser[]> {
        return this.apiService.post(`${controllerManageRoute}/get-agents`, payload).pipe(
            map((response: AgentUser[]) => {
                return response;
            }),
        );
    }

    public createAgent(payload: AgentUser): Observable<AgentUser> {
        return this.apiService.post(`${controllerManageRoute}/agent-create-agent`, payload).pipe(
            map((response: AgentUser) => {
                this.toastService.buildToastMessage(ToastType.success, ToastType.success, "New Agent Created!");
                return response;
            }),
        );
    }

    public editAgent(payload: AgentUser): Observable<AgentUser> {
        return this.apiService.post(`${controllerManageRoute}/edit-agent-profile`, payload).pipe(
            map((response: AgentUser) => {
                this.toastService.buildToastMessage(ToastType.success, ToastType.success, "Agent Info Updated!");
                return response;
            }),
        );
    }

    public resetAgentPassword(payload: AgentUser): Observable<AgentUser> {
        return this.apiService.post(`${controllerManageRoute}/edit-agent-reset-password`, payload).pipe(
            map((response: AgentUser) => {
                this.toastService.buildToastMessage(ToastType.success, "Agent Password", "New password generated and verification email sent!");
                return response;
            }),
        );
    }

    public updateDisabledAgents(payload: AgentsManageDisabled): Observable<AgentUser> {
        return this.apiService.post(`${controllerManageRoute}/update-agents-disabled`, payload).pipe(
            map((response: AgentUser) => {
                this.toastService.buildToastMessage(ToastType.success, "Agent Disabled", "Selected agents disabled or enabled updated!");
                return response;
            }),
        );
    }

    public getAgentCompaniesByBroker(payload: WorkingBroker): Observable<AgentCompany[]> {
        return this.apiService.post(`${controllerManageRoute}/get-agent-companies-by-broker`, payload).pipe(
            map((response: AgentCompanyList) => {
                return response.agentCompanies;
            }),
        );
    }

    public getCompanyAssignedAgents(payload: AgentCompany): Observable<AgentUser[]> {
        return this.apiService.post(`${controllerManageRoute}/get-agents-assigned-to-company`, payload).pipe(
            map((response: AgentUser[]) => {
                return response;
            }),
        );
    }

    public unAssignAgentsFromCompany(payload: CompanyAgentsSelected): Observable<null> {
        return this.apiService.post(`${controllerManageRoute}/un-assign-agents-from-company`, payload).pipe(
            map((response: any) => {
                this.toastService.buildToastMessage(ToastType.success, ToastType.success, "Selectd Agents unassigned from Company!");
                return null;
            }),
        );
    }

    public getAgentsNotAssignedToCompany(payload: AgentCompany): Observable<AgentUser[]> {
        return this.apiService.post(`${controllerManageRoute}/get-agents-not-assigned-to-company`, payload).pipe(
            map((response: AgentUser[]) => {
                return response;
            }),
        );
    }

    public assignAgentsToCompany(payload: CompanyAgentsSelected): Observable<null> {
        return this.apiService.post(`${controllerManageRoute}/assign-agents-to-company`, payload).pipe(
            map((response: any) => {
                this.toastService.buildToastMessage(ToastType.success, ToastType.success, "Assigned Agents to Company!");
                return null;
            }),
        );
    }

    public getCompaniesForAgent(payload: AgentUserFilterPayload): Observable<AgentCompany[]> {
        return this.apiService.post(`${controllerManageRoute}/get-companies-for-agent`, payload).pipe(
            map((response: AgentCompany[]) => {
                return response;
            }),
        );
    }

    public unAssignComapniesFromAgents(payload: AgentCompaniesSelected): Observable<null> {
        return this.apiService.post(`${controllerManageRoute}/un-assign-companies-from-agent`, payload).pipe(
            map((response: any) => {
                this.toastService.buildToastMessage(ToastType.success, ToastType.success, "Selectd Comapnies unassigned from Agent!");
                return null;
            }),
        );
    }

    public getCompaniesNotAssignedToAgent(payload: AgentUserFilterPayload): Observable<AgentCompany[]> {
        return this.apiService.post(`${controllerManageRoute}/get-companies-for-agent-unassigned`, payload).pipe(
            map((response: AgentCompany[]) => {
                return response;
            }),
        );
    }

    public assignCompaniesToAgents(payload: AgentCompaniesSelected): Observable<null[]> {
        return this.apiService.post(`${controllerManageRoute}/assign-companies-to-agent`, payload).pipe(
            map((response: any) => {
                return null;
            }),
        );
    }

    public getCarriers(): Observable<Carrier[]> {
        return this.apiService.post(`${controllerManageRoute}/get-agent-carriers`, null).pipe(
            map((response: Carrier[]) => {
                return response;
            }),
        );
    }

    public getAgentCarrierInfo(payload: CarrierPayload): Observable<AgentCarrierInfo[]> {
        return this.apiService.post(`${controllerManageRoute}/get-agent-carrier-info`, payload).pipe(
            map((response: AgentCarrierInfo[]) => {
                return response;
            }),
        );
    }

    public saveAgentCarrierInfo(payload: AgentCarrierInfoPayload): Observable<null> { 
        return this.apiService.post(`${controllerManageRoute}/save-agent-carrier-info`, payload).pipe(
            map((response: AgentCarrierInfo[]) => {
                this.toastService.buildToastMessage(ToastType.success, ToastType.success, "Agent Carrier Info Saved!");
                return null;
            }),
        );
    }

    public getCompaniesForCreateMember(): Observable<AgentCompany[]> {
        return this.apiService.get(`${controllerRoute}/enabled-companies/create-members`).pipe(
            map((response: AgentCompanyList) => {
                return response.agentCompanies;
            }),
        );
    }

    public getCreateMemberModelByCompany(payload: AgentUserFilterPayload): Observable<AgentCreateMemberModel> {
        return this.apiService.post(`${controllerRoute}/create-member-model`, payload).pipe(
            map((response: AgentCreateMemberModel) => {
                return response;
            }),
        );
    }

    public createMember(payload: AgentCreateMemberPayload): Observable<AgentCreateMemberResponse> {
        return this.apiService.post(`${controllerRoute}/create-member`, payload).pipe(
            map((response: AgentCreateMemberResponse) => {
                this.toastService.buildToastMessage(ToastType.success, ToastType.success, "New Member Created!");
                return response;
            }),
        );
    }

    public checkServerStatus(): Observable<ServerConnectionModel> {
        return this.apiService.get(`${controllerManageRoute}/check-server-status`).pipe(
            map((response: ServerConnectionModel) => {
                return response;
            }),
        );
    }

    public authenticatorPing(): Observable<ServerConnectionAuthModel> {
        return this.apiService.get(`${controllerManageRoute}/authenticator-ping`).pipe(map((response: ServerConnectionAuthModel) => {
            return response;
        }));
    }

    public authenticatorTest(payload: AuthenticatorRequest): Observable<Authenticator> {
        return this.apiService.post(`${controllerManageRoute}/authenticator-test`, payload).pipe(map((response: Authenticator) => {
            return response;
        }));
    }

    public authenticatorTestValidate(payload: AuthenticatorRequest): Observable<Authenticator> {
        return this.apiService.post(`${controllerManageRoute}/authenticator-test-validate`, payload).pipe(map((response: Authenticator) => {
            return response;
        }));
    }

    public authenticatorGenerateQR(payload: AuthenticatorRequest): Observable<AuthenticatorQR> {
        return this.apiService.post(`${controllerManageRoute}/authenticator-test-generate-qr`, payload).pipe(map((response: AuthenticatorQR) => {
            return response;
        }));
    }

    public authenticatorValidateQRCode(payload: AuthenticatorQR): Observable<Authenticator> {
        return this.apiService.post(`${controllerManageRoute}/authenticator-test-validate-qr`, payload).pipe(map((response: Authenticator) => {
            return response;
        }));
    }
}